import { requestJson } from "@/lib/api";

const BASE = "api/v1/admin/supplier-invoices/manifestation";

/** Tipos de evento da Manifestação do Destinatário (NT 2020.001). */
export const MANIFESTATION_EVENTS = {
  CIENCIA: "210210",
  CONFIRMACAO: "210200",
  DESCONHECIMENTO: "210220",
  NAO_REALIZADA: "210240",
} as const;

export type ManifestationEventType =
  (typeof MANIFESTATION_EVENTS)[keyof typeof MANIFESTATION_EVENTS];

export type ManifestationStatus = "PENDENTE" | "ACEITO" | "REJEITADO" | "INCERTO";

export interface ManifestationEvent {
  id: string;
  tpEvento: string;
  label: string;
  nSeqEvento: number;
  status: ManifestationStatus;
  justificativa: string | null;
  cStat: string | null;
  xMotivo: string | null;
  protocolo: string | null;
  createdAt: string;
  respondedAt: string | null;
  reconciledAt: string | null;
  reconcileReference: string | null;
  reconcileNote: string | null;
}

export interface ManifestationPreview {
  accessKey: string;
  emitterName: string | null;
  emitterDocument: string | null;
  nNf: string | null;
  totalNfe: string | null;
  emittedAt: string | null;
  tpAmb: number;
  tpEvento: string;
  label: string;
  meaning: string;
  conclusive: boolean;
  requiresJustification: boolean;
  allowed: boolean;
  blockReason: string | null;
  nextSeq: number;
  conclusiveDeadlineDays: number | null;
  cienciaDeadlineDays: number | null;
  /** Prazo é ESTIMATIVA (base emissão; autorização indisponível) — não afirma vencimento legal. */
  deadlineEstimated: boolean;
  cienciaKnownViaDistribution: boolean;
  /** Contagem local NÃO é autoritativa: histórico fiscal completo não é garantido (ver eventos recebidos). */
  countAuthoritative: boolean;
  hasUnresolvedUncertain: boolean;
  /** Sequência indeterminável com segurança (evento recebido sem protocolo) → envio bloqueado. */
  sequenceUncertain: boolean;
  history: ManifestationEvent[];
  /** Eventos conhecidos pela distribuição DF-e (evidência; não provam o nosso envio específico). */
  receivedEvents?: ReceivedManifestationEvent[];
  /** "Conhecido até" o último NSU sincronizado — chegar ao fim da fila não prova histórico completo. */
  knownUpToNsu?: string | null;
  knownUpToAt?: string | null;
}

export interface ReceivedManifestationEvent {
  tpEvento: string;
  label: string;
  nSeqEvento: number;
  authorDocument: string | null;
  cStat: string | null;
  protocolo: string | null;
  nsu: string;
  format: string;
}

export type ManifestOutcome =
  | "OK" | "REJECTED" | "UNCERTAIN" | "BLOCKED_UNCERTAIN" | "LOCKED" | "LIMIT_REACHED"
  | "IDEMPOTENT_HIT" | "NOT_FOUND" | "NOT_ELIGIBLE" | "JUSTIFICATION_REQUIRED" | "INVALID_EVENT"
  | "DISABLED" | "NOT_READY";

export interface ManifestResult {
  outcome: ManifestOutcome;
  message: string;
  event: ManifestationEvent | null;
}

export async function getManifestationPreview(
  accessKey: string, tpEvento: string): Promise<ManifestationPreview> {
  return requestJson<ManifestationPreview>(
    `${BASE}/preview?accessKey=${encodeURIComponent(accessKey)}&tpEvento=${encodeURIComponent(tpEvento)}`);
}

export async function manifest(req: {
  accessKey: string;
  tpEvento: string;
  justificativa?: string;
  requestId: string;
}): Promise<ManifestResult> {
  return requestJson<ManifestResult>(`${BASE}/manifest`, {
    method: "POST",
    body: {
      accessKey: req.accessKey,
      tpEvento: req.tpEvento,
      justificativa: req.justificativa ?? null,
      requestId: req.requestId,
    },
  });
}

/**
 * Conciliação MANUAL de um evento INCERTO (com base em verificação oficial externa; NÃO há consulta automática).
 * registered=true → confirma registro (conta ocorrência); false → confirma não registrado (libera nova tentativa).
 */
export async function reconcileManifestation(req: {
  eventId: string;
  registered: boolean;
  reference: string;
  protocolo?: string;
  note?: string;
}): Promise<ManifestResult> {
  return requestJson<ManifestResult>(`${BASE}/reconcile`, {
    method: "POST",
    body: {
      eventId: req.eventId,
      registered: req.registered,
      protocolo: req.protocolo ?? null,
      reference: req.reference,
      note: req.note ?? null,
    },
  });
}

export async function getManifestationHistory(accessKey: string): Promise<ManifestationEvent[]> {
  return requestJson<ManifestationEvent[]>(`${BASE}/${encodeURIComponent(accessKey)}/history`);
}

/** Alerta de prazo conclusivo (D6): NF sem conclusão conhecida, prazo próximo/vencido. Só exibição. */
export interface DeadlineAlert {
  accessKey: string;
  emitterName: string | null;
  nNf: string | null;
  emittedAt: string | null;
  conclusiveDeadlineDays: number;
  overdue: boolean;
}

export async function getManifestationAlerts(withinDays = 15): Promise<DeadlineAlert[]> {
  return requestJson<DeadlineAlert[]>(`${BASE}/alerts?withinDays=${withinDays}`);
}
